/* eslint-disable lingui/no-unlocalized-strings, lingui/text-restrictions */
import {
  motion,
  useMotionValueEvent,
  useReducedMotion,
  useScroll,
  useTransform,
} from "framer-motion";
import {
  FileSearch,
  type LucideIcon,
  MessageSquareText,
  ScanText,
  Sparkles,
  Users,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { thread } from "./content";

const STAGE_ICONS: LucideIcon[] = [FileSearch, MessageSquareText, ScanText, Users];

type Stage = (typeof thread.stages)[number];

const Block = ({
  side,
  content,
}: {
  side: "candidate" | "recruiter";
  content: Stage["candidate"];
}) => (
  <div className={`hub-block hub-block--${side}`}>
    <p className="hub-block__who">
      <span className="hub-dot" aria-hidden="true" />
      {side === "candidate" ? "Job seeker" : "Hiring team"}
    </p>
    <h3>{content.title}</h3>
    <p>{content.body}</p>
  </div>
);

const StageRow = ({
  stage,
  index,
  lit,
  rowRef,
}: {
  stage: Stage;
  index: number;
  lit: boolean;
  rowRef: (node: HTMLLIElement | null) => void;
}) => {
  const Icon = STAGE_ICONS[index];

  return (
    <li ref={rowRef} className="hub-stage-row" data-lit={lit} data-final={index === 3}>
      <Block side="candidate" content={stage.candidate} />
      <div className="hub-rail__node">
        <p className="hub-rail__label">{stage.label}</p>
        <span className="hub-node" aria-hidden="true">
          <Icon size={18} aria-hidden="true" />
        </span>
        <p className="hub-rail__ai">
          <Sparkles size={12} aria-hidden="true" /> {stage.ai}
        </p>
      </div>
      <Block side="recruiter" content={stage.recruiter} />
    </li>
  );
};

/** The two journeys side by side, joined by a thread that draws as you scroll. */
export const ThreadSection = () => {
  const reduced = useReducedMotion();
  const bodyRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: bodyRef,
    offset: ["start 62%", "end 58%"],
  });
  const draw = useTransform(scrollYProgress, (value) => `inset(0 0 ${(1 - value) * 100}% 0)`);

  // The spark rides the tip of the thread. Its position is a pixel translate (not `top`), so it
  // needs the rail's height.
  const [height, setHeight] = useState(0);
  useEffect(() => {
    const node = bodyRef.current;
    if (!node) return;
    const measure = () => setHeight(node.offsetHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  const sparkY = useTransform(scrollYProgress, (value) => value * height);

  // A stage lights when the thread's tip reaches its node, and stays lit. Driven by the thread's
  // own progress (not an intersection observer) so a fast scroll or an anchor jump can't skip one.
  const rows = useRef<(HTMLLIElement | null)[]>([]);
  const [litCount, setLitCount] = useState(0);
  const light = (progress: number) => {
    const body = bodyRef.current;
    if (!body) return;
    const tip = progress * body.offsetHeight;
    let count = 0;
    rows.current.forEach((row, index) => {
      if (row && tip >= row.offsetTop + row.offsetHeight / 2) count = index + 1;
    });
    setLitCount((previous) => Math.max(previous, count));
  };
  useMotionValueEvent(scrollYProgress, "change", light);
  useEffect(() => light(scrollYProgress.get()), [height]);

  return (
    <section className="hub-thread" aria-labelledby="hub-thread-title">
      <div className="hub-shell">
        <div className="hub-thread__intro">
          <div>
            <p className="hub-eyebrow">{thread.eyebrow}</p>
            <h2 id="hub-thread-title">{thread.title}</h2>
          </div>
          <p>{thread.lead}</p>
        </div>

        <div className="hub-thread__chairs" aria-hidden="true">
          <span className="hub-chair hub-chair--candidate">{thread.candidateChair}</span>
          <span className="hub-chair hub-chair--recruiter">{thread.recruiterChair}</span>
        </div>

        <div ref={bodyRef} className="hub-thread__body">
          <span className="hub-thread__line" aria-hidden="true" />
          <motion.span
            className="hub-thread__fill"
            aria-hidden="true"
            style={reduced ? undefined : { clipPath: draw }}
          />
          {!reduced && (
            <motion.span className="hub-thread__spark" aria-hidden="true" style={{ y: sparkY }} />
          )}
          <ol className="hub-thread__list">
            {thread.stages.map((stage, index) => (
              <StageRow
                key={stage.id}
                stage={stage}
                index={index}
                lit={Boolean(reduced) || index < litCount}
                rowRef={(node) => {
                  rows.current[index] = node;
                }}
              />
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
};
